import { useState } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import TablePagination from '@mui/material/TablePagination';
import FormControlLabel from '@mui/material/FormControlLabel';
import CircularProgress from '@mui/material/CircularProgress';

import { fDateTime } from 'src/utils/format-time';

import { useAccountRole } from 'src/hooks/auth/use-account-role';

import { AccountRole } from 'src/services/organisation/organisation.enum';
import {
  McpAuditOutcome,
  useGetMcpAuditQuery,
  useGetMcpSettingsQuery,
  useAcceptMcpTermsMutation,
  useDisconnectMcpMutation,
  useSetMcpEnabledMutation,
  useGetMcpConnectionsQuery,
} from 'src/services/ai/ai-mcp';

/**
 * The Claude connection in AI settings (JT1-1833): accept the terms (ADR 0006), switch it on, give
 * recruiters the address, see who is connected, and read the log of every lookup. Agency admins
 * only, and only for agencies with the AI layer.
 */

const TOOL_LABELS: Record<string, string> = {
  search_candidates: 'Searched candidates',
  get_candidate: 'Read a candidate',
  search_contacts: 'Searched contacts',
  get_contact: 'Read a contact',
  search_companies: 'Searched companies',
  get_company: 'Read a company',
  search_jobs: 'Searched jobs',
  get_job: 'Read a job',
  get_job_shortlist: 'Read a saved shortlist',
  search_applications: 'Searched applications',
  search_placements: 'Searched placements',
  search_deals: 'Searched deals',
  search_tasks: 'Searched tasks',
  search_notes: 'Read notes',
  search_interviews: 'Searched interviews',
  recent_activity: 'Read recent activity',
  list_reports: 'Listed reports',
  run_report: 'Ran a report',
};

const OUTCOMES: Record<McpAuditOutcome, { label: string; color: 'success' | 'error' | 'warning' | 'default' }> = {
  pending: { label: 'Running', color: 'default' },
  ok: { label: 'Done', color: 'success' },
  error: { label: 'Failed', color: 'error' },
  refused: { label: 'Switched off', color: 'default' },
  limited: { label: 'Over limit', color: 'warning' },
};

const SCOPES: Record<string, string> = {
  organization: 'whole agency',
  department: 'their departments',
  self: 'their own records',
};

export function ClaudeConnectionTerms() {
  return (
    <Typography component="ul" variant="body2" sx={{ pl: 2.5, m: 0, '& li': { mb: 0.75 } }}>
      <li>
        When a recruiter asks their own Claude about JobTetris records, what they ask about leaves JobTetris and is held in
        their Claude account, under your agency&apos;s agreement with Anthropic, not JobTetris&apos;s.
      </li>
      <li>
        Your agency decides which Claude plans its recruiters use. We recommend Claude Team or Enterprise, or a personal
        plan with model training switched off.
      </li>
      <li>
        Claude sees only what each recruiter can see in JobTetris, filtered by your AI Restrictions. It cannot change
        anything, and it never receives resumes or documents.
      </li>
      <li>Every lookup is logged below, and lookups are limited per recruiter so the connection cannot copy your database.</li>
    </Typography>
  );
}

export default function ClaudeConnectionSection() {
  const role = useAccountRole();
  const isAdmin = role === AccountRole.Admin || role === AccountRole.Op_Manager;
  const { currentData: settings, isLoading } = useGetMcpSettingsQuery(undefined, { skip: !isAdmin });

  if (!isAdmin || isLoading || !settings?.ai_layer) return null;

  return (
    <Card>
      <CardHeader
        title="Claude connection"
        subheader="Recruiters can connect their own Claude to JobTetris and ask it about their candidates, jobs and reports. Read only."
      />
      <CardContent>
        {settings.released ? <Released /> : <ComingSoon />}
      </CardContent>
    </Card>
  );
}

function ComingSoon() {
  return (
    <Alert severity="info">
      Coming soon. Once it is released, an admin can switch it on here after accepting the terms.
    </Alert>
  );
}

function Released() {
  const { currentData: settings } = useGetMcpSettingsQuery();
  const [acceptTerms, { isLoading: accepting }] = useAcceptMcpTermsMutation();
  const [setEnabled, { isLoading: switching }] = useSetMcpEnabledMutation();
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  if (!settings) return null;

  const run = async (action: () => Promise<unknown>) => {
    setFailed(null);
    try {
      await action();
    } catch (error: any) {
      setFailed(error?.data?.message ?? 'That did not save. Please try again.');
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(settings.mcp_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // The address is on screen to copy by hand.
    }
  };

  return (
    <Stack spacing={3}>
      <Stack spacing={1.5}>
        <Typography variant="subtitle2">Terms</Typography>
        <ClaudeConnectionTerms />
        {settings.terms_accepted ? (
          <Typography variant="body2" color="text.secondary">
            Accepted by {settings.terms_accepted_by ?? 'an admin'}
            {settings.terms_accepted_at ? ` on ${fDateTime(settings.terms_accepted_at)}` : ''}.
          </Typography>
        ) : (
          <Box>
            <Button variant="outlined" disabled={accepting} onClick={() => run(() => acceptTerms().unwrap())}>
              {accepting ? <CircularProgress size={20} /> : 'Accept the terms'}
            </Button>
          </Box>
        )}
      </Stack>

      <FormControlLabel
        control={
          <Switch
            checked={settings.enabled}
            disabled={!settings.terms_accepted || switching}
            onChange={(event) => run(() => setEnabled(event.target.checked).unwrap())}
          />
        }
        label={settings.enabled ? 'On: recruiters can connect Claude' : 'Off'}
      />
      {!settings.terms_accepted && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: -2 }}>
          Accept the terms to switch it on.
        </Typography>
      )}

      {failed && <Alert severity="error">{failed}</Alert>}

      {settings.enabled && (
        <>
          <Divider />
          <Stack spacing={1.5}>
            <Typography variant="subtitle2">How recruiters connect</Typography>
            <Typography variant="body2">
              In Claude, open Settings, then Connectors, then Add custom connector. Name it JobTetris, paste this address
              and sign in with their JobTetris login.
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField size="small" fullWidth value={settings.mcp_url} InputProps={{ readOnly: true }} />
              <Button variant="outlined" onClick={copy}>
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </Stack>
          </Stack>
        </>
      )}

      <Divider />
      <Connections />
      <Divider />
      <AuditLog />
    </Stack>
  );
}

function Connections() {
  const { currentData: connections = [], isLoading } = useGetMcpConnectionsQuery({ all: true });
  const [disconnect, { isLoading: disconnecting }] = useDisconnectMcpMutation();

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2">Connected recruiters</Typography>
      {isLoading && <CircularProgress size={20} />}
      {!isLoading && !connections.length && (
        <Typography variant="body2" color="text.secondary">
          Nobody has connected Claude yet.
        </Typography>
      )}
      {!!connections.length && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Recruiter</TableCell>
              <TableCell>App</TableCell>
              <TableCell>Connected</TableCell>
              <TableCell>Last used</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {connections.map((connection) => (
              <TableRow key={connection.id}>
                <TableCell>{connection.person ?? 'Unknown'}</TableCell>
                <TableCell>{connection.client_name}</TableCell>
                <TableCell>{fDateTime(connection.connected_at)}</TableCell>
                <TableCell>{connection.last_used_at ? fDateTime(connection.last_used_at) : 'Not yet'}</TableCell>
                <TableCell align="right">
                  <Button size="small" color="error" disabled={disconnecting} onClick={() => disconnect(connection.id)}>
                    Disconnect
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Stack>
  );
}

function AuditLog() {
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(25);
  const [accountId, setAccountId] = useState('');
  const { currentData: connections = [] } = useGetMcpConnectionsQuery({ all: true });
  const { currentData: audit, isLoading } = useGetMcpAuditQuery({
    page: page + 1,
    per_page: perPage,
    ...(accountId ? { account_id: accountId } : {}),
  });

  const people = Array.from(new Map(connections.map((c) => [c.account_id, c.person ?? 'Unknown'])).entries());

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Typography variant="subtitle2">Lookups</Typography>
        {!!people.length && (
          <TextField
            select
            size="small"
            label="Recruiter"
            value={accountId}
            onChange={(event) => {
              setAccountId(event.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">Everyone</MenuItem>
            {people.map(([id, name]) => (
              <MenuItem key={id} value={id}>
                {name}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Stack>
      {isLoading && <CircularProgress size={20} />}
      {!isLoading && !audit?.count && (
        <Typography variant="body2" color="text.secondary">
          No lookups yet.
        </Typography>
      )}
      {!!audit?.count && (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Recruiter</TableCell>
                <TableCell>Lookup</TableCell>
                <TableCell align="right">Records</TableCell>
                <TableCell>Result</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {audit.results.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{fDateTime(entry.created_at)}</TableCell>
                  <TableCell>{entry.person ?? 'Unknown'}</TableCell>
                  <TableCell>
                    {TOOL_LABELS[entry.tool] ?? entry.tool}
                    {entry.report_id ? `: ${entry.report_id}` : ''}
                    {entry.report_scope ? ` (${SCOPES[entry.report_scope] ?? entry.report_scope})` : ''}
                    {lookupDetail(entry.input)}
                  </TableCell>
                  <TableCell align="right">{entry.rows_returned}</TableCell>
                  <TableCell>
                    <Chip size="small" variant="soft" color={OUTCOMES[entry.outcome]?.color ?? 'default'} label={OUTCOMES[entry.outcome]?.label ?? entry.outcome} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={audit.count}
            page={page}
            rowsPerPage={perPage}
            rowsPerPageOptions={[25, 50, 100]}
            onPageChange={(_, next) => setPage(next)}
            onRowsPerPageChange={(event) => {
              setPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </>
      )}
    </Stack>
  );
}

/** What was searched for, in a few words: the name or description, when there was one. */
function lookupDetail(input: Record<string, unknown> | null): string {
  if (!input) return '';
  const words = ['name', 'query'].map((key) => input[key]).find((value) => typeof value === 'string' && value);
  return words ? `: "${String(words).slice(0, 60)}"` : '';
}
